import { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FiArrowLeft, FiCreditCard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ecommerceApi } from '../services/api';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

function EmbeddedPaymentForm({ onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!stripe || !elements) {
      setError('Stripe is still loading. Please try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/settings/billing`,
        },
      });

      if (result.error) {
        setError(result.error.message || 'Unable to save payment method.');
        return;
      }

      if (result.setupIntent?.status === 'succeeded') {
        await ecommerceApi.confirmPaymentMethodSetup(result.setupIntent.id);
        onSuccess();
        return;
      }

      setError('Setup did not complete successfully. Please try again.');
    } catch (confirmError) {
      setError(confirmError?.message || 'Unexpected error during payment setup.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <PaymentElement />

      <button
        type="submit"
        disabled={!stripe || !elements || isSubmitting}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-3 px-4 rounded-lg transition duration-200"
      >
        {isSubmitting ? 'Saving Payment Method...' : 'Save Payment Method'}
      </button>
    </form>
  );
}

export default function BillingSetup() {
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: 'stripe',
      },
    }),
    [clientSecret]
  );

  useEffect(() => {
    const loadSetupIntent = async () => {
      setLoading(true);
      setError('');
      try {
        if (!stripePublishableKey) {
          throw new Error('Missing REACT_APP_STRIPE_PUBLISHABLE_KEY');
        }

        const response = await ecommerceApi.createSetupIntent();
        if (!response?.clientSecret) {
          throw new Error('Missing client secret from setup-intent response.');
        }
        setClientSecret(response.clientSecret);
      } catch (setupError) {
        const message =
          setupError?.response?.data?.error ||
          setupError?.message ||
          'Unable to initialize billing setup.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadSetupIntent();
  }, []);

  const handleSuccess = () => {
    setSuccess('Payment method saved successfully.');
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate('/settings/billing')}
        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 mb-6"
      >
        <FiArrowLeft className="mr-2" />
        Back to Billing
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-8">
        <div className="flex items-center mb-4">
          <FiCreditCard className="w-6 h-6 text-gray-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">Billing Setup</h1>
        </div>

        <p className="text-gray-600 mb-6">Add your payment method securely with Stripe.</p>

        {loading ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-700 text-sm">
            Initializing secure payment form...
          </div>
        ) : null}

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm mb-4">
            {success}
          </div>
        ) : null}

        {!loading && !error && clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <EmbeddedPaymentForm onSuccess={handleSuccess} />
          </Elements>
        ) : null}
      </div>
    </div>
  );
}
